import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RemoveIcon from '@mui/icons-material/Remove';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { useUser } from '../../../contexts/UserContext';
import {
  dmarcApi,
  fmtNumber,
  fmtPct,
  fmtRelative,
  anomalyLevel,
  DomainSummary,
} from '../../../services/dmarcApi';

const TrendIcon: React.FC<{ trend: DomainSummary['trend'] }> = ({ trend }) => {
  if (trend === 'up')
    return <TrendingUpIcon fontSize="small" sx={{ color: 'success.main' }} />;
  if (trend === 'down')
    return <TrendingDownIcon fontSize="small" sx={{ color: 'error.main' }} />;
  if (trend === 'flat')
    return <RemoveIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
  return (
    <Typography component="span" color="text.secondary">
      —
    </Typography>
  );
};

const AnomalyChip: React.FC<{ row: DomainSummary }> = ({ row }) => {
  const level = anomalyLevel(row.pass_rate_30d, row.pass_rate_prior_30d);
  if (level === 'critical')
    return (
      <Chip
        size="small"
        icon={<ErrorOutlineIcon />}
        label="Major drop"
        color="error"
        variant="outlined"
      />
    );
  if (level === 'warning')
    return (
      <Chip
        size="small"
        icon={<ReportProblemIcon />}
        label="Watch"
        color="warning"
        variant="outlined"
      />
    );
  return null;
};

const DmarcOverview: React.FC = () => {
  const { isAdmin } = useUser();
  const navigate = useNavigate();
  const [domains, setDomains] = useState<DomainSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/app/welcome');
      return;
    }
  }, [isAdmin, navigate]);

  useEffect(() => {
    if (!isAdmin()) return;
    dmarcApi
      .listDomains()
      .then(r => setDomains(r.domains))
      .catch(e => setError(e instanceof Error ? e.message : 'load failed'));
  }, [isAdmin]);

  if (!isAdmin()) return null;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight={600}>
            DMARC overview
          </Typography>
          <Typography variant="body2" color="text.secondary">
            DMARC alignment across monitored domains
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Link component={RouterLink} to="/app/admin/dmarc/sources" underline="hover">
            Global sources
          </Link>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>Failed to load domains: {error}</Alert>}

      <Card>
        <CardContent>
          <Typography variant="h6" mb={2}>
            Monitored domains
          </Typography>
          {!domains && !error && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Box>
          )}
          {domains && domains.length === 0 && (
            <Typography color="text.secondary" align="center" py={4}>
              No monitored domains yet.
            </Typography>
          )}
          {domains && domains.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Domain</TableCell>
                    <TableCell align="right">Reports 30d</TableCell>
                    <TableCell align="right">Reports 7d</TableCell>
                    <TableCell align="right">Messages 30d</TableCell>
                    <TableCell align="right">Pass rate 30d</TableCell>
                    <TableCell align="center">Trend</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Last report</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {domains.map(d => (
                    <TableRow hover key={d.domain}>
                      <TableCell sx={{ fontWeight: 500 }}>
                        <Link
                          component={RouterLink}
                          to={`/app/admin/dmarc/domains/${encodeURIComponent(d.domain)}`}
                          underline="hover"
                        >
                          {d.domain}
                        </Link>
                      </TableCell>
                      <TableCell align="right">{fmtNumber(d.report_count_30d)}</TableCell>
                      <TableCell align="right">{fmtNumber(d.report_count_7d)}</TableCell>
                      <TableCell align="right">{fmtNumber(d.messages_30d)}</TableCell>
                      <TableCell align="right" sx={{ fontFeatureSettings: '"tnum"' }}>
                        {fmtPct(d.pass_rate_30d)}
                      </TableCell>
                      <TableCell align="center">
                        <TrendIcon trend={d.trend} />
                      </TableCell>
                      <TableCell>
                        {d.last_seen_iso ? (
                          <AnomalyChip row={d} />
                        ) : (
                          <Chip size="small" variant="outlined" label="Awaiting first report" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {fmtRelative(d.last_seen_iso)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {domains && domains.length > 0 && !domains.some(d => d.last_seen_iso) && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No reports have arrived yet. DMARC reports typically begin landing within 24 hours of
            a domain's DMARC record pointing reports to{' '}
            <Box component="code" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
              reports.travelminds.com
            </Box>
            .
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default DmarcOverview;
